import React, { useState } from "react";
import api from "../lib/api";
import { Repeat, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ASSETS = ["BTC", "ETH", "SOL", "XRP", "BNB", "ADA", "DOGE", "AVAX", "DOT", "HYPE"];

export default function DcaCalculator() {
  const [asset, setAsset] = useState("BTC");
  const [amount, setAmount] = useState("50");
  const [frequency, setFrequency] = useState("weekly");
  const [startDate, setStartDate] = useState("2022-01-01");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/tools/dca-calculator", {
        params: { asset, amount_usd: amt, start_date: startDate, frequency },
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not calculate");
    } finally { setLoading(false); }
  };

  const isProfit = result && result.profit_usd >= 0;

  return (
    <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <Repeat className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">DCA Calculator</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
          See what dollar-cost averaging into a coin on a regular schedule would be worth today.
        </p>
      </div>

      <form onSubmit={calculate} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Coin</label>
            <select value={asset} onChange={(e) => setAsset(e.target.value)}
              className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
              {ASSETS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Frequency</label>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
              className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Amount per buy $</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="mt-2 w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
          </div>
        </div>
        <button type="submit" disabled={loading}
          className="mt-4 w-full bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 transition disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
          Calculate
        </button>
      </form>

      {result && (
        <div className={"mt-6 border p-6 " + (isProfit ? "border-green-400/30 bg-green-400/5" : "border-red-400/30 bg-red-400/5")}>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Investing <span className="text-[var(--text-primary)] font-bold">${result.amount_per_purchase.toLocaleString()}</span> in{" "}
            <span className="text-[var(--text-primary)] font-bold">{result.asset}</span> every {result.frequency === "weekly" ? "week" : "month"} since{" "}
            <span className="text-[var(--text-primary)] font-bold">{result.start_date}</span> ({result.purchases_made} purchases):
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Total Invested</div>
              <div className="font-display font-bold text-2xl text-[var(--text-primary)]">${result.total_invested.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Current Value</div>
              <div className="font-display font-bold text-2xl text-[var(--text-primary)]">${result.current_value.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Profit</div>
              <div className={"font-display font-bold text-2xl flex items-center gap-1 " + (isProfit ? "text-green-400" : "text-red-400")}>
                {isProfit ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                {isProfit ? "+" : ""}{result.profit_pct}%
              </div>
            </div>
          </div>

          <p className="text-xs font-mono text-[var(--text-tertiary)]">
            {result.total_coins} {result.asset} accumulated at an average price of ${result.avg_price_paid.toLocaleString()}, now worth ${result.current_price.toLocaleString()} each.
          </p>
        </div>
      )}

      <p className="mt-8 text-xs text-[var(--text-tertiary)] text-center">
        Prices from Binance historical daily data. Past performance does not guarantee future results. Not financial advice.
      </p>

      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How this calculator works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">1. Set a schedule</div>
            <p className="text-xs text-[var(--text-secondary)]">Choose a coin, a fixed dollar amount, how often you'd buy, and when you would have started.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">2. Simulate every buy</div>
            <p className="text-xs text-[var(--text-secondary)]">The calculator looks up the actual market price on each scheduled purchase date and works out how many coins that buy would have gotten.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">3. Total it up</div>
            <p className="text-xs text-[var(--text-secondary)]">All coins from every purchase are added together and valued at today's price, showing your total invested, current value, and return.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          Dollar-cost averaging means investing a fixed amount on a regular schedule regardless of price, rather than trying to time the market with a single lump sum. This spreads out purchases across highs and lows, which tends to smooth out the impact of volatility over time.
        </p>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          Results use daily closing prices and do not account for exchange fees, taxes, or slippage on real trades. Very long weekly ranges are capped at 500 purchases, use monthly frequency for multi-year simulations. This tool is for illustration only and is not financial advice.
        </p>
      </div>
    </div>
  );
}